import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/appIcon';
import Button from '../../../components/ui/Button';

const CARD =
    'bg-gray-900/80 border border-gray-800/90 rounded-2xl shadow-inner shadow-black/40';
const PANEL =
    'bg-gradient-to-b from-gray-950 via-gray-950/95 to-gray-900 border border-gray-800/90 rounded-2xl shadow-[0_25px_80px_rgba(0,0,0,0.65)]';

const TONE = {
    primary: {
        iconWrap: 'bg-cyan-500/15 border border-cyan-400/40',
        icon: 'text-cyan-300',
        badgeWrap: 'bg-cyan-500/10 border border-cyan-500/30',
        badgeText: 'text-cyan-200'
    },
    success: {
        iconWrap: 'bg-emerald-500/15 border border-emerald-400/40',
        icon: 'text-emerald-300',
        badgeWrap: 'bg-emerald-500/10 border border-emerald-500/30',
        badgeText: 'text-emerald-200'
    },
    warning: {
        iconWrap: 'bg-amber-500/15 border border-amber-400/40',
        icon: 'text-amber-300',
        badgeWrap: 'bg-amber-500/10 border border-amber-500/30',
        badgeText: 'text-amber-200'
    },
    error: {
        iconWrap: 'bg-red-500/15 border border-red-400/40',
        icon: 'text-red-300',
        badgeWrap: 'bg-red-500/10 border border-red-500/30',
        badgeText: 'text-red-200'
    },
    muted: {
        iconWrap: 'bg-gray-700/30 border border-gray-600/40',
        icon: 'text-gray-300',
        badgeWrap: 'bg-gray-800/50 border border-gray-700',
        badgeText: 'text-gray-200'
    }
};

const EmptyState = ({ title = 'Sin datos', desc = 'Ajusta el rango o carga información.' }) => (
    <div className={CARD + ' p-6 flex items-center justify-center'}>
        <div className="text-center max-w-md">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mb-3">
                <Icon name="Database" size={22} className="text-cyan-300" />
            </div>
            <p className="text-white font-semibold">{title}</p>
            <p className="text-sm text-gray-400 mt-1">{desc}</p>
        </div>
    </div>
);

const defaultTrendIcon = (trend) => {
    if (trend === 'up') return { icon: 'TrendingUp', cls: 'text-emerald-300' };
    if (trend === 'down') return { icon: 'TrendingDown', cls: 'text-red-300' };
    return { icon: 'Minus', cls: 'text-gray-400' };
};

const normalizeStatusTone = (status) => {
    // Puedes mapearlo a tu lógica real.
    // Ejemplo: high/medium/low -> success/warning/error
    if (status === 'high') return 'success';
    if (status === 'medium') return 'warning';
    if (status === 'low') return 'error';
    return 'muted';
};

/**
 * client shape esperado (flexible):
 * {
 *   id: string|number,
 *   name: string,
 *   segment?: string,
 *   revenue?: number,
 *   profit?: number,
 *   profitMargin?: number,  // si no viene, lo calculo si hay revenue/profit
 *   orders?: number,
 *   avgOrderValue?: number,
 *   paymentTerms?: number,
 *   status?: 'high'|'medium'|'low'|string,
 *   trend?: 'up'|'down'|'stable'|string,
 *   contact?: string
 * }
 */
const ClientReports = ({
    dateRange,
    onDateRangeChange,
    onExport,

    clients = [], // ✅ datos reales
    loading = false,
    error = null,

    locale = 'es-MX',
    defaultCurrency = 'MXN'
}) => {
    const [viewType, setViewType] = useState('profitability');
    const [currency, setCurrency] = useState(defaultCurrency);

    const formatCurrency = (value) =>
        new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            minimumFractionDigits: 0
        }).format(Number(value) || 0);

    const enrichedClients = useMemo(() => {
        return (clients ?? []).map((c) => {
            const revenue = Number(c.revenue) || 0;
            const profit = Number(c.profit) || 0;
            const profitMargin =
                c.profitMargin != null
                    ? Number(c.profitMargin)
                    : revenue > 0
                        ? (profit / revenue) * 100
                        : 0;

            return {
                ...c,
                revenue,
                profit,
                profitMargin
            };
        });
    }, [clients]);

    const totals = useMemo(() => {
        const totalRevenue = enrichedClients.reduce((sum, c) => sum + (Number(c.revenue) || 0), 0);
        const totalProfit = enrichedClients.reduce((sum, c) => sum + (Number(c.profit) || 0), 0);
        const avgMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

        return { totalRevenue, totalProfit, avgMargin };
    }, [enrichedClients]);

    const segments = useMemo(() => {
        // Agrupar por segment si viene
        const map = new Map();
        for (const c of enrichedClients) {
            const seg = c.segment || 'Sin segmento';
            const cur = map.get(seg) || { segment: seg, clients: 0, revenue: 0, profit: 0 };
            cur.clients += 1;
            cur.revenue += Number(c.revenue) || 0;
            cur.profit += Number(c.profit) || 0;
            map.set(seg, cur);
        }
        return Array.from(map.values()).sort((a, b) => b.profit - a.profit);
    }, [enrichedClients]);

    if (loading) {
        return (
            <div className="space-y-4">
                <div className={PANEL + ' p-5'}>
                    <div className="h-5 w-56 bg-gray-800/70 rounded-lg" />
                    <div className="h-4 w-72 bg-gray-900/70 rounded-lg mt-2" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {[0, 1, 2, 3].map((i) => (
                        <div key={i} className={CARD + ' p-5'}>
                            <div className="h-11 w-11 bg-gray-800/60 rounded-xl mb-3" />
                            <div className="h-7 w-40 bg-gray-800/60 rounded-lg" />
                            <div className="h-4 w-32 bg-gray-900/60 rounded-lg mt-2" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return <EmptyState title="Error al cargar" desc={String(error)} />;
    }

    if (!enrichedClients || enrichedClients.length === 0) {
        return <EmptyState title="Sin clientes" desc="No hay clientes para este rango." />;
    }

    const metrics = [
        { label: 'Clientes', value: enrichedClients.length, icon: 'Users', tone: 'primary', isMoney: false },
        { label: 'Ingresos', value: totals.totalRevenue, icon: 'DollarSign', tone: 'success', isMoney: true },
        { label: 'Utilidad', value: totals.totalProfit, icon: 'TrendingUp', tone: 'primary', isMoney: true },
        { label: 'Margen Promedio', value: totals.avgMargin.toFixed(1) + '%', icon: 'Percent', tone: 'warning', isMoney: false }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className={PANEL + ' p-4 sm:p-5'}>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg sm:text-xl font-bold text-white truncate">Reportes por Cliente</h3>
                            <span className="inline-flex text-[11px] font-mono px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/40">
                                {dateRange?.start || '—'} → {dateRange?.end || '—'}
                            </span>
                        </div>
                        <p className="text-cyan-200/80 text-xs sm:text-sm truncate">
                            Rentabilidad y actividad por cliente
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-2 justify-end">
                        {/* Toggle view */}
                        <div className="inline-flex items-center gap-1 bg-gray-950/50 border border-gray-800 rounded-xl p-1">
                            {[
                                { id: 'profitability', label: 'Rentabilidad', icon: 'TrendingUp' },
                                { id: 'activity', label: 'Actividad', icon: 'Activity' }
                            ].map((t) => {
                                const active = viewType === t.id;
                                return (
                                    <button
                                        key={t.id}
                                        onClick={() => setViewType(t.id)}
                                        className={[
                                            'px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all flex items-center gap-2',
                                            active
                                                ? 'bg-cyan-500/15 border border-cyan-500/40 text-cyan-200 shadow-[0_0_18px_rgba(34,211,238,0.25)]'
                                                : 'text-gray-300 hover:text-white hover:bg-gray-900/70'
                                        ].join(' ')}
                                    >
                                        <Icon name={t.icon} className="w-4 h-4" />
                                        {t.label}
                                    </button>
                                );
                            })}
                        </div>

                        <select
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value)}
                            className="bg-gray-950/50 border border-gray-800 rounded-xl px-3 py-2 text-xs sm:text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                        >
                            <option value="MXN">MXN $</option>
                            <option value="USD">USD $</option>
                            <option value="ARS">ARS $</option>
                        </select>

                        <Button
                            variant="outline"
                            onClick={() => onExport?.('clients')}
                            className="border-gray-700 text-gray-300 hover:bg-gray-900 hover:text-white"
                        >
                            <Icon name="Download" className="w-4 h-4 mr-2" />
                            Exportar
                        </Button>
                    </div>
                </div>
            </div>

            {/* Métricas */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {metrics.map((m, i) => {
                    const st = TONE[m.tone] ?? TONE.primary;
                    return (
                        <motion.div
                            key={m.label}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.06 }}
                            className={CARD + ' p-4 sm:p-5'}
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${st.iconWrap}`}>
                                    <Icon name={m.icon} size={20} className={st.icon} />
                                </div>
                            </div>

                            <p className="text-xl sm:text-2xl font-bold text-white mb-1 font-mono">
                                {m.isMoney ? formatCurrency(m.value) : m.value}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-400">{m.label}</p>
                        </motion.div>
                    );
                })}
            </div>

            {/* Tabla */}
            <div className={CARD + ' p-4 sm:p-6'}>
                <div className="flex items-center justify-between gap-3 mb-4">
                    <div className="min-w-0">
                        <h4 className="text-sm sm:text-base font-semibold text-white">
                            {viewType === 'profitability' ? 'Rentabilidad por Cliente' : 'Actividad por Cliente'}
                        </h4>
                        <p className="text-[11px] text-gray-400">Ordena, filtra y exporta desde aquí</p>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full min-w-[860px]">
                        <thead>
                            <tr className="border-b border-gray-800/80">
                                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                    Cliente
                                </th>
                                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                    {viewType === 'profitability' ? 'Ingresos' : 'Pedidos'}
                                </th>
                                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                    {viewType === 'profitability' ? 'Utilidad' : 'Valor Promedio'}
                                </th>
                                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                    {viewType === 'profitability' ? 'Margen' : 'Términos Pago'}
                                </th>
                                <th className="text-center py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                    Estado
                                </th>
                                <th className="text-center py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                    Tendencia
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {enrichedClients.map((client) => {
                                const statusTone = TONE[normalizeStatusTone(client.status)] ?? TONE.muted;
                                const tr = defaultTrendIcon(client.trend);

                                return (
                                    <tr
                                        key={client.id ?? client.name}
                                        className="border-b border-gray-800/50 last:border-0 hover:bg-gray-950/40"
                                    >
                                        <td className="py-3 px-4">
                                            <div className="min-w-0">
                                                <p className="font-semibold text-gray-100 text-sm truncate">
                                                    {client.name || 'Sin nombre'}
                                                </p>
                                                <p className="text-xs text-gray-400 truncate">
                                                    {client.segment || '—'}
                                                </p>
                                            </div>
                                        </td>

                                        <td className="py-3 px-4 text-sm text-right text-gray-100 font-mono">
                                            {viewType === 'profitability'
                                                ? formatCurrency(client.revenue)
                                                : Number(client.orders) || 0}
                                        </td>

                                        <td className="py-3 px-4 text-sm text-right text-gray-100 font-mono">
                                            {viewType === 'profitability'
                                                ? formatCurrency(client.profit)
                                                : formatCurrency(client.avgOrderValue)}
                                        </td>

                                        <td className="py-3 px-4 text-sm text-right text-gray-100 font-mono">
                                            {viewType === 'profitability'
                                                ? (Number(client.profitMargin) || 0).toFixed(1) + '%'
                                                : (Number(client.paymentTerms) || 0) + ' días'}
                                        </td>

                                        <td className="py-3 px-4 text-center">
                                            <span
                                                className={[
                                                    'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold',
                                                    statusTone.badgeWrap,
                                                    statusTone.badgeText
                                                ].join(' ')}
                                            >
                                                <Icon name="Circle" size={8} />
                                                {client.status ?? '—'}
                                            </span>
                                        </td>

                                        <td className="py-3 px-4 text-center">
                                            <Icon name={tr.icon} size={16} className={tr.cls} />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Segmentación (real, basada en tus datos) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className={CARD + ' p-4 sm:p-6'}>
                    <div className="flex items-center gap-2 mb-4">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-cyan-500/15 border border-cyan-400/40">
                            <Icon name="PieChart" size={18} className="text-cyan-300" />
                        </div>
                        <div>
                            <h4 className="text-sm sm:text-base font-semibold text-white">Segmentación</h4>
                            <p className="text-[11px] text-gray-400">Agrupación por segmento (si viene)</p>
                        </div>
                    </div>

                    {segments.length === 0 ? (
                        <p className="text-xs text-gray-500">No hay segmentación disponible.</p>
                    ) : (
                        <div className="space-y-3">
                            {segments.map((seg) => {
                                const margin = seg.revenue > 0 ? (seg.profit / seg.revenue) * 100 : 0;
                                return (
                                    <div
                                        key={seg.segment}
                                        className="flex items-center justify-between p-3 bg-gray-950/60 border border-gray-800 rounded-2xl"
                                    >
                                        <div>
                                            <p className="font-semibold text-gray-100 text-sm">{seg.segment}</p>
                                            <p className="text-xs text-gray-400">{seg.clients} cliente(s)</p>
                                        </div>

                                        <div className="text-right">
                                            <p className="text-sm font-bold text-gray-100 font-mono">
                                                {formatCurrency(seg.profit)}
                                            </p>
                                            <p className="text-xs text-gray-400">{margin.toFixed(1)}% margen</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Insights (sin inventar) */}
                <div className={CARD + ' p-4 sm:p-6'}>
                    <div className="flex items-center gap-2 mb-4">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-emerald-500/15 border border-emerald-400/40">
                            <Icon name="Lightbulb" size={18} className="text-emerald-300" />
                        </div>
                        <div>
                            <h4 className="text-sm sm:text-base font-semibold text-white">Insights</h4>
                            <p className="text-[11px] text-gray-400">Top clientes por utilidad (real)</p>
                        </div>
                    </div>

                    <div className="space-y-3">
                        {enrichedClients
                            .slice()
                            .sort((a, b) => (b.profit || 0) - (a.profit || 0))
                            .slice(0, 5)
                            .map((c) => (
                                <div
                                    key={c.id ?? c.name}
                                    className="flex items-start gap-3 p-3 bg-gray-950/60 border border-gray-800 rounded-2xl"
                                >
                                    <Icon name="User" size={16} className="text-cyan-300 mt-0.5" />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-gray-100 text-sm truncate">{c.name}</p>
                                        <p className="text-xs text-gray-400 truncate">
                                            Utilidad: <span className="text-gray-200 font-mono">{formatCurrency(c.profit)}</span>
                                            {' • '}
                                            Margen: <span className="text-gray-200 font-mono">{(c.profitMargin || 0).toFixed(1)}%</span>
                                        </p>
                                        {c.contact ? (
                                            <p className="text-[11px] text-gray-500 truncate mt-1">{c.contact}</p>
                                        ) : null}
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientReports;
